export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: unknown
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, body: ApiErrorBody) {
    super(body.error?.message ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.error?.code ?? 'onbekend'
    this.details = body.error?.details
  }
}

type Query = Record<string, string | number | boolean | undefined | null>

function withQuery(path: string, query?: Query): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 204) return undefined as T

  const contentType = res.headers.get('content-type') ?? ''
  const isJson = contentType.includes('json')

  if (!res.ok) {
    const fallback: ApiErrorBody = {
      error: { code: `http_${res.status}`, message: res.statusText || 'Onbekende fout' },
    }
    const parsed = isJson ? await res.json().catch(() => fallback) : fallback
    throw new ApiError(res.status, parsed as ApiErrorBody)
  }

  if (isJson) return (await res.json()) as T
  return (await res.text()) as unknown as T
}

async function upload<T>(path: string, veld: string, bestand: File): Promise<T> {
  const formulier = new FormData()
  formulier.append(veld, bestand)

  const res = await fetch(path, { method: 'POST', credentials: 'include', body: formulier })
  const isJson = (res.headers.get('content-type') ?? '').includes('json')

  if (!res.ok) {
    const fallback: ApiErrorBody = {
      error: { code: `http_${res.status}`, message: res.statusText || 'Onbekende fout' },
    }
    const parsed = isJson ? await res.json().catch(() => fallback) : fallback
    throw new ApiError(res.status, parsed as ApiErrorBody)
  }

  return (await res.json()) as T
}

export const http = {
  get: <T>(path: string, query?: Query) => request<T>('GET', withQuery(path, query)),
  post: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>('POST', withQuery(path, query), body),
  patch: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>('PATCH', withQuery(path, query), body),
  put: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>('PUT', withQuery(path, query), body),
  del: <T>(path: string, query?: Query) => request<T>('DELETE', withQuery(path, query)),
  upload,
}

export function errorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback
  if (Array.isArray(error.details)) {
    const first = error.details.find(
      (item): item is { message: string } =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { message?: unknown }).message === 'string',
    )
    if (first) return first.message
  }
  return error.message || fallback
}

export const gebruiktMockApi = import.meta.env.VITE_MOCK_API === '1'
